import { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Logo } from "@/components/Logo";

export default function AdminLogin() {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/admin/dashboard" replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(email, password);
      toast.success("Bienvenue dans votre espace WebUP.");
      navigate("/admin/dashboard");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Identifiants incorrects.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="min-h-screen bg-paper-soft grid grid-cols-1 lg:grid-cols-2">
      <aside className="hidden lg:flex flex-col justify-between bg-ink text-white p-12 relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-50" aria-hidden />
        <div className="relative">
          <Logo invert />
        </div>
        <div className="relative">
          <p className="font-mono-label text-white/55"><span className="rule" />Espace administrateur</p>
          <h1 className="mt-6 font-display text-5xl leading-[1.05] tracking-tight">
            Toutes vos demandes de devis, <em className="italic font-light">au même endroit</em>.
          </h1>
          <p className="mt-6 text-white/65 leading-relaxed max-w-md">
            Consultez, triez, annotez et exportez les leads générés par votre
            site WebUP — depuis un back-office sobre et performant.
          </p>
        </div>
        <p className="relative text-xs text-white/40">© {new Date().getFullYear()} WebUP — Marssac-sur-Tarn</p>
      </aside>

      <div className="flex items-center justify-center p-8">
        <form onSubmit={onSubmit} className="w-full max-w-md bg-white border border-ink/10 p-10">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-ink/55 hover:text-ink mb-8">
            <ArrowLeft size={14} /> Retour au site
          </Link>
          <p className="font-mono-label text-ink/55">Connexion</p>
          <h2 className="mt-3 font-display text-3xl text-ink">Accès administrateur</h2>
          <p className="mt-2 text-sm text-ink/60">Réservé à l’équipe WebUP.</p>

          <div className="mt-8 space-y-5">
            <div>
              <label className="block text-xs font-mono-label text-ink/60 mb-2">Email</label>
              <input
                data-testid="admin-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full bg-paper-soft border border-ink/15 h-12 px-4 text-ink focus:outline-none focus:border-ink"
              />
            </div>
            <div>
              <label className="block text-xs font-mono-label text-ink/60 mb-2">Mot de passe</label>
              <input
                data-testid="admin-password"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full bg-paper-soft border border-ink/15 h-12 px-4 text-ink focus:outline-none focus:border-ink"
              />
            </div>
          </div>

          <button
            type="submit"
            data-testid="admin-login-submit"
            disabled={submitting}
            className="mt-8 w-full inline-flex items-center justify-center gap-2 h-12 bg-ink text-white text-sm font-medium hover:bg-ink-600 transition-colors disabled:opacity-60"
          >
            {submitting ? "Connexion…" : "Se connecter"}
            <ArrowUpRight size={16} />
          </button>
        </form>
      </div>
    </div>
  );
}
